import { createFileRoute } from "@tanstack/react-router";
import { MisSolicitudesPage } from "@/features/solicitudes/components/MisSolicitudesPage";
import { misSolicitudesQueryOptions } from "@/features/solicitudes/hooks/use-mis-solicitudes";
import type { QuickFilter } from "@/features/solicitudes/components/SolicitudesStats";
import { ErrorAlert } from "@/components/ui";
import { PermissionGate } from "@/shared/auth/PermissionGate";
import { VER_SOLICITUDES } from "@/lib/permisos";
import { useAuthStore } from "@/lib/auth-store";

const QUICK_FILTERS: ReadonlyArray<string> = [
  "requierenAccion",
  "enContabilizacion",
  "enAutorizacion",
  "enCaptura",
  "canceladas",
];

/** Categorías de resultado (deep-link del donut) · nombres del enum del backend. */
const CATEGORIAS: ReadonlyArray<string> = [
  "EnProceso",
  "EnAutorizacion",
  "Autorizada",
  "Cancelada",
  "RechazadaPorReglas",
  "RechazadaPorSat",
  "BajaMesaControl",
  "DescartadaPlazoVencido",
];

interface SolicitudesSearch {
  /** Quick-filter de los cards de la lista (deep-link desde los KPIs del dashboard). */
  filtro?: Exclude<QuickFilter, null>;
  /** Categoría de resultado (deep-link desde el donut "Distribución · últimos 3 meses"). */
  categoria?: string;
  /** Muestra canceladas/dadas de baja (mismo toggle del header · lo activan las categorías terminales). */
  incluir?: boolean;
}

export const Route = createFileRoute("/_authed-tw/solicitudes/")({
  // Deep-links opcionales: `?filtro=` (quick-filter de los KPIs) o `?categoria=`
  // (donut del dashboard, con `?incluir=true` para las canceladas/baja). Los
  // valores inválidos/ausentes se descartan.
  validateSearch: (search: Record<string, unknown>): SolicitudesSearch => {
    const out: SolicitudesSearch = {};
    if (typeof search.filtro === "string" && QUICK_FILTERS.includes(search.filtro)) {
      out.filtro = search.filtro as Exclude<QuickFilter, null>;
    }
    if (typeof search.categoria === "string" && CATEGORIAS.includes(search.categoria)) {
      out.categoria = search.categoria;
    }
    if (search.incluir === true || search.incluir === "true") out.incluir = true;
    return out;
  },
  // El endpoint `/solicitudes/mias` responde 403 a quien no puede ver solicitudes
  // (ej. Visor). Si el loader lo dispara igual, el throw del 403 corre ANTES del
  // PermissionGate del componente y deja la ruta en blanco. Por eso el loader solo
  // precarga cuando el usuario tiene el permiso; el resto cae directo en el gate.
  loader: ({ context: { queryClient } }) => {
    const permisos = useAuthStore.getState().user?.permisos ?? [];
    if (!VER_SOLICITUDES.some((p) => permisos.includes(p))) return;
    return queryClient.ensureQueryData(misSolicitudesQueryOptions());
  },
  component: () => (
    <PermissionGate permisos={VER_SOLICITUDES}>
      <MisSolicitudesPage />
    </PermissionGate>
  ),
  errorComponent: ({ error }) => <ErrorAlert error={error} />,
});
